<template>
  <div id="app">
    <!-- Admin pages use their own layout -->
    <template v-if="isAdminRoute">
      <stale-chunk-note v-if="staleRouteUrl" variant="page" :url="staleRouteUrl" />
      <router-view/>
    </template>

    <!-- Public site layout -->
    <template v-else>
      <nav class="main-nav">
        <div class="nav-container">
          <div class="logo">
            <router-link to="/" class="wordmark" aria-label="Cruise Signals home">
              <svg class="mark" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true" focusable="false">
                <circle cx="14" cy="14" r="12" fill="none" stroke="currentColor" stroke-width="2"/>
                <path d="M6 16c3-6 7-8 8-8s5 2 8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <circle cx="14" cy="10" r="2" fill="currentColor"/>
              </svg>
              <span>Cruise Signals</span>
            </router-link>
          </div>
          <div class="nav-right">
            <button
              type="button"
              class="theme-toggle"
              :aria-label="darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
              :title="darkMode ? 'Light mode' : 'Dark mode'"
              @click="toggleDarkMode"
            >
              <i class="fas" :class="darkMode ? 'fa-sun' : 'fa-moon'"></i>
            </button>
            <button
              type="button"
              class="nav-toggle"
              aria-label="Toggle menu"
              :aria-expanded="navOpen"
              @click="navOpen = !navOpen"
            >
              <i class="fas" :class="navOpen ? 'fa-times' : 'fa-bars'"></i>
            </button>
          </div>
          <div class="nav-links" :class="{ 'nav-open': navOpen }">
            <router-link to="/" @click="navOpen = false">Home</router-link>
            <router-link to="/cruises" @click="navOpen = false">Browse Cruises</router-link>
            <router-link to="/best-deals" @click="navOpen = false">Best Deals</router-link>
            <router-link to="/hot-deals" @click="navOpen = false">Hot Deals</router-link>
            <router-link to="/comparison" @click="navOpen = false">Compare Cruise Lines</router-link>
            <router-link to="/manage-alerts" @click="navOpen = false">Manage Alerts</router-link>
          </div>
        </div>
      </nav>

      <main class="main-content">
        <!-- CP-274: a route chunk failed again within a minute of the one automatic reload (main.js). -->
        <stale-chunk-note v-if="staleRouteUrl" variant="page" :url="staleRouteUrl" />
        <router-view/>
      </main>

      <!-- CP-125: brand block + link columns. Reuses existing footer copy, nav labels, and routes only. -->
      <footer class="main-footer">
        <div class="footer-content">
          <div class="footer-grid">
            <div class="footer-brand-block">
              <router-link to="/" class="footer-wordmark" aria-label="Cruise Signals home">
                <svg class="mark" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true" focusable="false">
                  <circle cx="14" cy="14" r="12" fill="none" stroke="currentColor" stroke-width="2"/>
                  <path d="M6 16c3-6 7-8 8-8s5 2 8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <circle cx="14" cy="10" r="2" fill="currentColor"/>
                </svg>
                <span>Cruise Signals</span>
              </router-link>
              <p class="footer-disclaimer">All prices are in USD and subject to change.</p>
              <!-- CP-132: commission claim only while Affiliate/Book is ON. -->
              <p v-if="showCommissionLine" class="footer-affiliate">
                Cruise Signals may earn a commission when you book through links on this site, at no extra cost to you. See our
                <router-link to="/disclosure">Affiliate Disclosure</router-link>
                and
                <router-link to="/privacy">Privacy Policy</router-link>.
              </p>
            </div>
            <div class="footer-columns">
              <nav class="footer-col" aria-label="Site">
                <router-link to="/">Home</router-link>
                <router-link to="/cruises">Browse Cruises</router-link>
                <router-link to="/best-deals">Best Deals</router-link>
                <router-link to="/hot-deals">Hot Deals</router-link>
                <router-link to="/comparison">Compare Cruise Lines</router-link>
                <router-link to="/manage-alerts">Manage Alerts</router-link>
              </nav>
              <nav class="footer-col footer-links" aria-label="Legal">
                <router-link to="/privacy">Privacy</router-link>
                <router-link to="/terms">Terms</router-link>
                <router-link to="/disclosure">Affiliate</router-link>
              </nav>
            </div>
          </div>
          <p class="footer-brand">© {{ new Date().getFullYear() }} Cruise Signals</p>
        </div>
      </footer>
    </template>

    <affiliate-toast />
  </div>
</template>

<script>
import AffiliateToast from '@/components/AffiliateToast.vue';
import StaleChunkNote from '@/components/StaleChunkNote.vue';
import { NOTE_EVENT } from '@/utils/chunkReload';
import { affiliateLinkTracker, loadAffiliateFlag, showCommissionFooter } from '@/utils/affiliateFlag';

const STORAGE_KEY = 'cruise-tracker-theme';

export default {
  name: 'App',
  components: {
    AffiliateToast,
    StaleChunkNote
  },
  data() {
    return {
      navOpen: false,
      darkMode: false,
      staleRouteUrl: ''
    };
  },
  computed: {
    // CP-212: the footer commission line is render-driven: it shows while at least one affiliate
    // link (CruiseDirect or Viator, each marked v-affiliate-link) is rendered on the current page.
    showCommissionLine() {
      return showCommissionFooter(affiliateLinkTracker.rendered);
    },
    isAdminRoute() {
      return this.$route.path.startsWith('/admin');
    }
  },
  watch: {
    '$route'() {
      this.navOpen = false;
      this.staleRouteUrl = ''; // a later navigation succeeded
    }
  },
  created() {
    loadAffiliateFlag();
  },
  mounted() {
    window.addEventListener(NOTE_EVENT, this.onStaleRoute);
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || (saved === null && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      this.darkMode = true;
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      this.darkMode = false;
      document.documentElement.removeAttribute('data-theme');
    }
  },
  beforeUnmount() {
    window.removeEventListener(NOTE_EVENT, this.onStaleRoute);
  },
  methods: {
    onStaleRoute(e) {
      this.staleRouteUrl = (e && e.detail && e.detail.url) || window.location.href;
    },
    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      if (this.darkMode) {
        document.documentElement.setAttribute('data-theme', 'dark');
        localStorage.setItem(STORAGE_KEY, 'dark');
      } else {
        document.documentElement.removeAttribute('data-theme');
        localStorage.setItem(STORAGE_KEY, 'light');
      }
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.main-nav {
  background-color: var(--cs-nav-bg);
  color: white;
  padding: var(--space-3) 0;
  box-shadow: var(--shadow-nav);
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-4);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.wordmark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: white;
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  text-decoration: none;
  transition: opacity var(--transition-fast);
  min-height: var(--touch-min);
}

.wordmark:hover {
  opacity: 0.9;
}

.mark {
  flex-shrink: 0;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.theme-toggle,
.nav-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--touch-min);
  height: var(--touch-min);
  padding: 0;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 1.1rem;
  transition: background var(--transition-fast);
}

.theme-toggle:hover,
.nav-toggle:hover {
  background: rgba(255, 255, 255, 0.25);
}

.theme-toggle:focus-visible,
.nav-toggle:focus-visible {
  outline: 2px solid white;
  outline-offset: 2px;
}

/* Phone (base ≤640): hamburger drawer */
.nav-toggle {
  display: flex;
}

.nav-links {
  display: none;
  flex-direction: column;
  width: 100%;
  gap: 0;
  padding: var(--space-3) 0;
  order: 3;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.nav-links.nav-open {
  display: flex;
}

.nav-links a {
  color: white;
  text-decoration: none;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-sm);
  transition: background var(--transition-normal);
  font-weight: 500;
  font-family: var(--font-heading);
  min-height: var(--touch-min);
  display: inline-flex;
  align-items: center;
  width: 100%;
}

.nav-links a:hover {
  background: rgba(255, 255, 255, 0.15);
}

.nav-links a.router-link-active {
  /* CP-212: 0.18 keeps white 15px text at 5.0:1 on the teal bar (0.25 was 4.24:1). */
  background: rgba(255, 255, 255, 0.18);
  font-weight: 600;
}

.nav-links a:focus-visible {
  outline: 2px solid white;
  outline-offset: 2px;
}

.main-content {
  min-height: calc(100vh - 160px);
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-4) var(--space-4) var(--space-6);
}

.main-footer {
  background-color: var(--cs-signal-deep);
  color: white;
  padding: var(--space-6) 0 var(--space-5);
  margin-top: var(--space-6);
}

/* CP-125: phone = stacked brand block, then link columns side by side, then © bar */
.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  font-size: var(--text-small);
}

.footer-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-5);
}

.footer-brand-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
}

.footer-wordmark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: white;
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  text-decoration: none;
  min-height: var(--touch-min);
}

.footer-wordmark:hover {
  opacity: 0.9;
}

.footer-wordmark:focus-visible,
.footer-col a:focus-visible,
.footer-affiliate a:focus-visible {
  outline: 2px solid white;
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.footer-disclaimer {
  margin: 0;
  opacity: 0.9;
}

.footer-affiliate {
  margin: 0;
  max-width: 42rem;
  opacity: 0.95;
  line-height: var(--line-height-body);
}

.footer-affiliate a {
  color: white;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.footer-affiliate a:hover {
  opacity: 1;
}

.footer-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
}

.footer-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.footer-col a {
  color: white;
  text-decoration: none;
  min-height: var(--touch-min);
  display: inline-flex;
  align-items: center;
  opacity: 0.9;
  transition: opacity var(--transition-fast);
}

.footer-col a:hover {
  opacity: 1;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.footer-brand {
  margin: 0;
  padding-top: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-small);
  opacity: 0.9;
}

/* CP-230: on a phone the Detail page has a fixed 56px Book bar (BookNowButton, shown at <=767px).
   At max scroll it covered the © line. Only a page that renders that bar gets the extra room, so
   Home / Browse / deals footers are unchanged. :has() unsupported = no padding (status quo). */
@media (max-width: 767px) {
  #app:has(.sticky-book-bar) .main-footer {
    padding-bottom: calc(var(--space-5) + 56px + env(safe-area-inset-bottom, 0px));
  }
}

/* Tablet+ : horizontal nav */
@media (min-width: 768px) {
  .nav-toggle {
    display: none;
  }

  .nav-links {
    display: flex;
    flex-direction: row;
    width: auto;
    order: 0;
    padding: 0;
    border-top: none;
    gap: var(--space-2);
  }

  .nav-links a {
    width: auto;
    justify-content: center;
    padding: var(--space-2) var(--space-3);
  }

  .nav-container {
    padding: 0 var(--space-5);
  }

  .main-content {
    padding: var(--space-5) var(--space-5) var(--space-6);
  }

  /* CP-125 desktop: brand block left, link columns right */
  .footer-content {
    padding: 0 var(--space-5);
  }

  .footer-grid {
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
    gap: var(--space-6);
    align-items: start;
  }

  .footer-columns {
    gap: var(--space-5);
  }

  .wordmark {
    font-size: 1.5rem;
  }
}

/* Pointer devices: tighter link rhythm in footer columns (touch keeps 44px) */
@media (min-width: 768px) and (pointer: fine) {
  .footer-col a {
    min-height: 32px;
  }
}
</style>
